import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rating } from '@mui/material';
import {
  propertyApi,
  placesApi,
  billingApi,
  type PropertyData,
  GewType,
} from '../services';
import { useToast } from '../contexts/ToastContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  DatePicker,
  GrundstueckIcon,
  HausIcon,
  WohnungNeubauIcon,
  WohnungBestandIcon,
} from '../components';
import './PropertyPage.css';
import '../components/PropertyFiles.css';
import './WelcomePage.css';

const CreatePropertyPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [checkingLimit, setCheckingLimit] = useState<boolean>(true);
  const [isLimitReached, setIsLimitReached] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const checkLimit = async () => {
      try {
        const stats = await billingApi.getUsage();
        if (isMounted && stats && stats.properties) {
          if (
            typeof stats.properties.max === 'number' &&
            stats.properties.max > 0 &&
            stats.properties.used >= stats.properties.max
          ) {
            setIsLimitReached(true);
          }
        }
      } catch (err) {
        console.error('Failed to check property limit:', err);
      } finally {
        if (isMounted) {
          setCheckingLimit(false);
        }
      }
    };

    checkLimit();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isLimitReached) {
      const timer = setTimeout(() => {
        navigate('/shop');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isLimitReached, navigate]);

  const emptyProperty: PropertyData = {
    id: '',
    address: '',
    country: 'Österreich',
    gain: 0,
    gainPercentage: 0,
    features: {},
    ratings: {},
  };

  const [property, setProperty] = useState<PropertyData>(emptyProperty);
  const [step, setStep] = useState<number>(1);
  const [saving, setSaving] = useState<boolean>(false);
  const [config, setConfig] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(false);
  const [stock, setStock] = useState<string>('');
  const [plz, setPlz] = useState<string>('');
  const [zinssatzText, setZinssatzText] = useState<string>(
    property.zinssatz !== undefined
      ? String(property.zinssatz).replace('.', ',')
      : '',
  );

  useEffect(() => {
    if (property.zinssatz === undefined && zinssatzText !== '') {
      setZinssatzText('');
    } else if (
      property.zinssatz !== undefined &&
      parseFloat(zinssatzText.replace(',', '.')) !== property.zinssatz
    ) {
      setZinssatzText(String(property.zinssatz).replace('.', ','));
    }
  }, [property.zinssatz]);

  const [uploadProgressText, setUploadProgressText] = useState<string>('');
  const getConnectorStyle = (idx: number) => {
    if (idx < step - 1) {
      return { background: 'var(--Purple-Light, #571aff)' };
    }
    if (idx === step - 1) {
      return {
        background:
          'linear-gradient(90deg, var(--Purple-Light, #571aff) 0%, #ffffff 100%)',
      };
    }
    if (idx === step) {
      return { background: 'linear-gradient(90deg, #ffffff 0%, #a7a3e2 100%)' };
    }
    return { background: '#a7a3e2' };
  };

  // Fetch configuration whenever gewType changes
  useEffect(() => {
    if (property.gewType) {
      setLoadingConfig(true);
      propertyApi
        .getConditionClassificationConfig(property.gewType)
        .then((config) => {
          setConfig(config);

          // Dynamically initialize features and ratings based on config
          const initialFeatures: Record<
            string,
            { value: any; localization: string }
          > = {};
          config.weights?.features?.forEach((f: any) => {
            initialFeatures[f.id] = {
              value: false,
              localization: f.localization,
            };
          });
          config.weights?.enumFeatures?.forEach((f: any) => {
            let defaultValue = '';
            if (f.options && f.options.length > 0) {
              const sortDirection = f.sortDirection === 'desc' ? -1 : 1;
              const sortedOptions = [...f.options].sort((a, b) => (a.value - b.value) * sortDirection);
              defaultValue = sortedOptions[0].id;
            }
            initialFeatures[f.id] = {
              value: defaultValue,
              localization: f.localization,
            };
          });
          config.staticValues?.features?.forEach((f: any) => {
            initialFeatures[f.id] = {
              value: false,
              localization: f.localization,
            };
          });

          const initialRatings: Record<
            string,
            { value: number; localization: string }
          > = {};
          config.weights?.ratings?.forEach((r: any) => {
            initialRatings[r.id] = { value: 1, localization: r.localization };
          });
          config.weights?.numericRatings?.forEach((r: any) => {
            initialRatings[r.id] = {
              value: r.referenceValue ?? 1.0,
              localization: r.localization,
            };
          });

          setProperty((prev) => ({
            ...prev,
            features: initialFeatures,
            ratings: initialRatings,
          }));
        })
        .catch((err) => {
          console.error('Failed to load condition classification config:', err);
          showError(
            'Failed to load configuration for the selected property type.',
          );
        })
        .finally(() => {
          setLoadingConfig(false);
        });
    }
  }, [property.gewType]);

  // Suggestions state for autocomplete
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<number>(-1);

  const [isGeocoding, setIsGeocoding] = useState<boolean>(false);

  // Function to geocode an address using Google Maps Geocoder
  const geocodeAddress = (
    address: string,
  ): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      try {
        const googleMaps = (window as any).google;
        if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Geocoder) {
          resolve(null);
          return;
        }

        // Safety timeout so geocoding never hangs indefinitely
        const timeoutId = setTimeout(() => {
          console.warn('Geocoding timed out after 5 seconds');
          resolve(null);
        }, 5000);

        const geocoder = new googleMaps.maps.Geocoder();
        const geocodePromise = geocoder.geocode(
          { address, region: 'at' },
          (
            results: google.maps.GeocoderResult[] | null,
            status: google.maps.GeocoderStatus,
          ) => {
            clearTimeout(timeoutId);
            if (status === 'OK' && results && results.length > 0) {
              const location = results[0].geometry.location;
              resolve({
                lat: location.lat(),
                lng: location.lng(),
              });
            } else {
              resolve(null);
            }
          },
        );

        if (geocodePromise && typeof geocodePromise.catch === 'function') {
          geocodePromise.catch((err: any) => {
            clearTimeout(timeoutId);
            console.error('Geocoder promise rejection:', err);
            resolve(null);
          });
        }
      } catch (err) {
        console.error('Error initiating geocoding:', err);
        resolve(null);
      }
    });
  };

  // Refs for debounce timeout
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle address input change with debouncing for autocomplete
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProperty((prev) => ({
      ...prev,
      streetName: value,
      latitude: undefined,
      longitude: undefined,
    }));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!value) {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await placesApi.getAutocomplete(value, 'at');
        setSuggestions(result);
        setSelectedSuggestionIndex(-1);
      } catch (error) {
        console.error(error);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  // Handle city input change
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProperty((prev) => ({
      ...prev,
      city: value,
      latitude: undefined,
      longitude: undefined,
    }));
  };


  // Helper function to parse address components from Places API response
  const parseAddressComponents = (
    addressComponents: any[],
  ): {
    streetName?: string;
    houseNumber?: string;
    city?: string;
    country?: string;
    sublocality?: string;
    postalCode?: string;
    administrativeArea3?: string;
    administrativeArea2?: string;
  } => {
    const result: {
      streetName?: string;
      houseNumber?: string;
      city?: string;
      country?: string;
      sublocality?: string;
      postalCode?: string;
      administrativeArea3?: string;
      administrativeArea2?: string;
    } = {};

    if (!addressComponents || !Array.isArray(addressComponents)) {
      return result;
    }

    for (const component of addressComponents) {
      const types = component.types || [];
      const longText = component.longText;
      const shortText = component.shortText;
      const text = component.text;
      const value = longText || shortText || text;

      if (types.includes('street_number')) {
        result.houseNumber = value;
      } else if (types.includes('route')) {
        result.streetName = value;
      } else if (types.includes('locality') || types.includes('postal_town')) {
        result.city = value;
      } else if (types.includes('country')) {
        result.country = value;
      } else if (
        types.includes('sublocality') ||
        types.includes('sublocality_level_1') ||
        types.includes('neighborhood')
      ) {
        result.sublocality = value;
      } else if (types.includes('postal_code')) {
        result.postalCode = value;
      } else if (types.includes('administrative_area_level_3')) {
        result.administrativeArea3 = value;
      } else if (types.includes('administrative_area_level_2')) {
        result.administrativeArea2 = value;
      }
    }

    return result;
  };

  // Handle suggestion selection
  const handleSuggestionClick = async (suggestion: any) => {
    const placeId = suggestion.placeId || suggestion.placePrediction?.placeId;
    const address =
      suggestion.text?.text ||
      suggestion.placePrediction?.text?.text ||
      suggestion.text ||
      suggestion.placePrediction?.structuredFormat?.mainText?.text ||
      'Unknown address';

    setSuggestions([]);
    setSelectedSuggestionIndex(-1);

    if (placeId) {
      try {
        const details = await placesApi.getPlaceDetails(placeId);

        if (details?.location) {
          const lat = details.location.latitude;
          const lng = details.location.longitude;

          const addressComponents = details.addressComponents || [];
          const parsed = parseAddressComponents(addressComponents);
          const fullAddress = details.formattedAddress || address;

          if (parsed.postalCode) {
            setPlz(parsed.postalCode);
          }

          let matchedGemNr: number | undefined;
          let matchedGemName = '';
          try {
            const location = await placesApi.getLocationByCoordinates(
              lat,
              lng,
            );
            if (location && location.gewGemNr) {
              matchedGemNr = location.gewGemNr;
              matchedGemName = location.gewGemName || '';
            }
          } catch (err) {
            console.error('Error fetching location by coordinates:', err);
          }

          setProperty((prev) => {
            const updated = {
              ...prev,
              address: fullAddress,
              country: 'Österreich',
              latitude: lat,
              longitude: lng,
              gewGemNr: matchedGemNr,
            };

            if (parsed.streetName !== undefined) {
              updated.streetName = parsed.streetName;
            }
            if (parsed.houseNumber !== undefined) {
              updated.houseNumber = parsed.houseNumber;
            }

            if (parsed.city) {
              updated.city = parsed.city;
            } else if (matchedGemName) {
              updated.city = matchedGemName;
            } else {
              updated.city = '';
            }

            return updated;
          });
        } else {
          showError('Location coordinates could not be retrieved.');
          setProperty((prev) => ({ ...prev, address }));
        }
      } catch (error) {
        console.error('Error fetching place details:', error);
        showError('Failed to get address details.');
        setProperty((prev) => ({ ...prev, address }));
      }
    } else {
      setProperty((prev) => ({ ...prev, address }));
    }
  };

  // Cleanup autocomplete debouncer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Keyboard navigation for suggestions dropdown list
  const handleSuggestionsKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (
        selectedSuggestionIndex >= 0 &&
        selectedSuggestionIndex < suggestions.length
      ) {
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setUploadProgressText('Immobilie wird angelegt...');
    try {
      const createData = { ...property };
      delete (createData as any).id;
      delete (createData as any).gain;
      delete (createData as any).gainPercentage;

      createData.fremdfinanziert = !!createData.fremdfinanziert;

      // Format address to include PLZ and Stock if available
      // Street HouseNumber/Stock, PLZ City, Country
      const addressParts = [];
      const streetAndHouse = [property.streetName, property.houseNumber]
        .filter(Boolean)
        .join(' ');
      if (streetAndHouse) {
        if (stock) {
          addressParts.push(`${streetAndHouse}/${stock}`);
        } else {
          addressParts.push(streetAndHouse);
        }
      }
      const plzAndCity = [plz, property.city].filter(Boolean).join(' ');
      if (plzAndCity) {
        addressParts.push(plzAndCity);
      }
      addressParts.push(property.country || 'Österreich');
      createData.address = addressParts.join(', ');

      const newProperty = await propertyApi.create(createData as any);

      showSuccess('Immobilie erfolgreich hinzugefügt');
      navigate(`/properties/${newProperty.id}`);
    } catch (error: any) {
      showError(error.message || 'Fehler beim Speichern der Immobilie');
    } finally {
      setSaving(false);
      setUploadProgressText('');
    }
  };

  const getSortedFeatures = (config: any) => {
    if (!config) return [];
    const list = [
      ...(config.weights?.features || []),
      ...(config.staticValues?.features || []),
    ];
    const order = [
      'elevator',
      'balcony',
      'garage',
      'basement',
      'accessibility',
      'silentEnvironment',
      'publicTransport',
      'nearSupermarket',
      'thermallyRenovated',
      'modernHeating',
      'kitchen',
    ];
    return list.sort((a, b) => {
      const idxA = order.indexOf(a.id);
      const idxB = order.indexOf(b.id);
      return (idxA !== -1 ? idxA : 999) - (idxB !== -1 ? idxB : 999);
    });
  };

  const renderFeatureToggle = (fieldName: string, label: string) => {
    const value = property.features?.[fieldName]?.value === true;
    return (
      <div className="valuera-switch-container" key={fieldName}>
        <div className="valuera-switch-label">{label}</div>
        <div
          className="valuera-switch-wrapper"
          onClick={() => {
            setProperty((prev) => ({
              ...prev,
              features: {
                ...prev.features,
                [fieldName]: {
                  value: !value,
                  localization: label,
                },
              },
            }));
          }}
        >
          <div className={`valuera-switch-track ${value ? 'on' : 'off'}`}>
            <div className="valuera-switch-circle" />
          </div>
          <span className="valuera-switch-status">{value ? 'Ja' : 'Nein'}</span>
        </div>
      </div>
    );
  };

  const renderRatingInput = (fieldName: string, label: string) => {
    const value = property.ratings?.[fieldName]?.value || 1;
    return (
      <div className="valuera-rating-container" key={fieldName}>
        <div className="valuera-rating-label">{label}</div>
        <Rating
          name={fieldName}
          value={value}
          max={3}
          precision={1}
          size="large"
          sx={{
            color: '#faaf00',
            fontSize: '24px',
            '& .MuiRating-icon': {
              marginRight: '8px',
            },
            '& .MuiRating-iconFilled': { color: '#faaf00' },
            '& .MuiRating-iconHover': { color: '#faaf00' },
            '& .MuiRating-iconEmpty': { color: 'rgba(167, 163, 226, 0.2)' },
          }}
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              setProperty((prev) => ({
                ...prev,
                ratings: {
                  ...prev.ratings,
                  [fieldName]: {
                    value: newValue || 1,
                    localization: label,
                  },
                },
              }));
            }
          }}
        />
      </div>
    );
  };

  const renderNumericRatingInput = (
    fieldName: string,
    label: string,
    step: number = 0.1,
  ) => {
    const value = property.ratings?.[fieldName]?.value ?? 1.0;
    return (
      <div className="valuera-rating-container" key={fieldName}>
        <div className="valuera-rating-label">{label}</div>
        <div className="wizard-field-input-box" style={{ maxWidth: '200px' }}>
          <input
            type="number"
            step={step || 0.1}
            min={0}
            className="wizard-input"
            value={value !== undefined ? value : ''}
            onChange={(e) => {
              const raw = e.target.value;
              const val = raw === '' ? 0 : parseFloat(raw);
              setProperty((prev) => ({
                ...prev,
                ratings: {
                  ...prev.ratings,
                  [fieldName]: {
                    value: isNaN(val) ? 0 : val,
                    localization: label,
                  },
                },
              }));
            }}
          />
        </div>
      </div>
    );
  };

  const CustomEnumDropdown: React.FC<{
    featureConfig: any;
    value: string;
    onChange: (val: string, label: string) => void;
  }> = ({ featureConfig, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const sortDirection = featureConfig.sortDirection === 'desc' ? -1 : 1;
    const sortedOptions = featureConfig.options ? [...featureConfig.options].sort((a, b) => (a.value - b.value) * sortDirection) : [];

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const selectedOpt = sortedOptions.find(o => o.id === value) || sortedOptions[0];

    return (
      <div className="valuera-rating-container" key={featureConfig.id} ref={ref}>
        <div className="valuera-rating-label">{featureConfig.localization}</div>
        <div 
          className="wizard-field" 
          style={{ position: 'relative', maxWidth: '200px' }}
        >
          <div 
            className="wizard-field-input-box" 
            onClick={() => setOpen(!open)}
            style={{ cursor: 'pointer' }}
          >
            <div 
              className="wizard-input" 
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {selectedOpt ? selectedOpt.localization : 'Bitte wählen...'}
            </div>
            <span className="wizard-input-icon">
              <svg
                width="10"
                height="5"
                viewBox="0 0 10 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 0L5 5L10 0H0Z" fill="#F3F1FF" />
              </svg>
            </span>
          </div>

          {open && (
            <ul className="suggestions-container" style={{ width: '100%', minWidth: '180px' }}>
              {sortedOptions.map((opt, index) => {
                const isSelected = opt.id === value;
                return (
                  <li
                    key={index}
                    onClick={() => {
                      onChange(opt.id, featureConfig.localization);
                      setOpen(false);
                    }}
                    className={`suggestion-item ${isSelected ? 'selected' : ''}`}
                  >
                    <div className="suggestion-main-text">
                      {opt.localization}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderEnumFeatureDropdown = (featureConfig: any) => {
    const fieldName = featureConfig.id;
    const value = property.features?.[fieldName]?.value || '';
    
    return (
      <CustomEnumDropdown
        key={fieldName}
        featureConfig={featureConfig}
        value={value}
        onChange={(val, label) => {
          setProperty((prev) => ({
            ...prev,
            features: {
              ...prev.features,
              [fieldName]: {
                value: val,
                localization: label,
              },
            },
          }));
        }}
      />
    );
  };

  // Step Navigations
  const nextStep = async () => {
    if (step === 1 && !property.gewType) return;
    if (step === 2) {
      if (!isStep2Valid()) return;
      if (isGeocoding) return;

      setIsGeocoding(true);
      try {
        const city = property.city?.trim() || '';

        // If coordinates or gewGemNr are missing, fetch them before proceeding
        let lat = property.latitude;
        let lng = property.longitude;
        let fullAddress = property.address;
        let matchedGemNr = property.gewGemNr;

        if (
          lat === undefined ||
          lng === undefined ||
          lat === null ||
          lng === null
        ) {
          try {
            const parts = [];
            if (property.streetName) parts.push(property.streetName);
            if (property.houseNumber) parts.push(property.houseNumber);
            const cityPart = [plz, city].filter(Boolean).join(' ');
            if (cityPart) parts.push(cityPart);
            parts.push(property.country || 'Österreich');
            fullAddress = parts.join(', ');

            const location = await geocodeAddress(fullAddress);
            if (location) {
              lat = location.lat;
              lng = location.lng;
            } else {
              console.warn('Geocoding returned no result');
            }
          } catch (error) {
            console.error('Error geocoding address:', error);
          }
        }

        // If we have coordinates but no gewGemNr, resolve it from the GeoJSON
        if (lat !== undefined && lng !== undefined && !matchedGemNr) {
          try {
            const loc = await placesApi.getLocationByCoordinates(lat, lng);
            if (loc && loc.gewGemNr) {
              matchedGemNr = loc.gewGemNr;
            }
          } catch (err) {
            console.error('Error resolving location by coordinates:', err);
          }
        }

        if (!matchedGemNr) {
          showError('Der Ort konnte nicht in der österreichischen Gemeindedatenbank gefunden werden. Bitte überprüfe deine Eingabe.');
          return;
        }

        setProperty((prev) => ({
          ...prev,
          city,
          address: fullAddress || prev.address,
          latitude: lat ?? undefined,
          longitude: lng ?? undefined,
          gewGemNr: matchedGemNr ?? prev.gewGemNr,
        }));
        setStep((prev) => prev + 1);
      } finally {
        setIsGeocoding(false);
      }
      return;
    }
    if (step === 4 && !isStep4Valid()) return;
    setStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
  };

  // Step Validations
  const isStep2Valid = () => {
    const isWohnung =
      property.gewType === GewType.NEUBAU ||
      property.gewType === GewType.BESTAND;
    const isHaus =
      property.gewType === GewType.HAUS_NEUBAU ||
      property.gewType === GewType.HAUS_BESTAND;
    const isGrundstueck = property.gewType === GewType.GRUNDSTUECK;

    const isAreaValid = isWohnung
      ? property.area !== undefined && property.area > 0
      : isHaus
        ? property.area !== undefined &&
          property.area > 0 &&
          property.plotArea !== undefined &&
          property.plotArea > 0
        : isGrundstueck
          ? property.plotArea !== undefined && property.plotArea > 0
          : false;

    return (
      !!property.streetName?.trim() &&
      !!property.houseNumber?.trim() &&
      !!property.city?.trim() &&
      isAreaValid
    );
  };

  const isStep4Valid = () => {
    const hasKaufpreis = property.kaufpreis !== undefined;
    const hasKaufdatum = !!property.kaufdatum?.trim();

    if ((hasKaufpreis && !hasKaufdatum) || (!hasKaufpreis && hasKaufdatum)) {
      return false;
    }

    if (property.fremdfinanziert) {
      if (
        property.eigenkapital !== undefined &&
        property.fremdkapital !== undefined &&
        property.kaufpreis !== undefined
      ) {
        if (
          property.eigenkapital + property.fremdkapital !==
          property.kaufpreis
        ) {
          return false;
        }
      }
    }
    return true;
  };

  if (checkingLimit) {
    return (
      <div className="welcome-page">
        <div className="welcome-container">
          <div className="activation-spinner"></div>
          <h1 className="welcome-title">Wird geladen...</h1>
          <p style={{ color: '#a7a3e2', fontSize: '18px', margin: 0 }}>
            Überprüfung des Immobilienlimits...
          </p>
        </div>
      </div>
    );
  }

  if (isLimitReached) {
    return (
      <div className="welcome-page">
        <div className="welcome-container">
          <div
            className="activation-warning-icon"
            style={{
              fontSize: '32px',
              color: '#faaf00',
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(250, 175, 0, 0.1)',
              border: '2px solid rgba(250, 175, 0, 0.3)',
            }}
          >
            !
          </div>
          <h1 className="welcome-title">Immobilienlimit erreicht</h1>
          <p style={{ color: '#a7a3e2', fontSize: '18px', margin: 0 }}>
            Du hast das Limit für Immobilien in deinem aktuellen Plan erreicht. Du wirst in Kürze zum Shop weitergeleitet...
          </p>
          <button
            className="welcome-button"
            onClick={() => navigate('/shop')}
          >
            <span className="welcome-button-text">Direkt zum Shop</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="property-page">
      <div className="property-container">

        <div className="property-content">
          <h1
            className="property-title"
            style={{ textAlign: 'center', marginBottom: '24px' }}
          >
            Neue Immobilie hinzufügen
          </h1>

          {/* Stepper Progress Indicator */}
          <div className="wizard-stepper">
            <div className="stepper-steps">
              <div
                className={`step-item ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}
              >
                <div className="step-dot"></div>
                <div className="step-label">Typ</div>
              </div>

              <div
                className="step-connector"
                style={getConnectorStyle(1)}
              ></div>

              <div
                className={`step-item ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}
              >
                <div className="step-dot"></div>
                <div className="step-label">Allgemein</div>
              </div>

              <div
                className="step-connector"
                style={getConnectorStyle(2)}
              ></div>

              <div
                className={`step-item ${step === 3 ? 'active' : step > 3 ? 'completed' : ''}`}
              >
                <div className="step-dot"></div>
                <div className="step-label">Ausstattung & Zustand</div>
              </div>

              <div
                className="step-connector"
                style={getConnectorStyle(3)}
              ></div>

              <div
                className={`step-item ${step === 4 ? 'active' : step > 4 ? 'completed' : ''}`}
              >
                <div className="step-dot"></div>
                <div className="step-label">Finanzierung</div>
              </div>
            </div>
          </div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Step 1: GewType Selection */}
            {step === 1 && (
              <div className="gewtype-container" style={{ width: '100%' }}>
                <div className="gewtype-grid">
                  {/* Card 1: Grundstück */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.GRUNDSTUECK ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.GRUNDSTUECK,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <GrundstueckIcon />
                    </div>
                    <div className="gewtype-card-title">Grundstück</div>
                  </div>

                  {/* Card 2: Zinshaus (Disabled) */}
                  <div className="gewtype-card disabled">
                    <div className="gewtype-card-icon">
                      <HausIcon />
                    </div>
                    <div className="gewtype-card-title">Zinshaus</div>
                  </div>

                  {/* Card 3: Haus (Neubau) */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.HAUS_NEUBAU ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.HAUS_NEUBAU,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <HausIcon />
                    </div>
                    <div className="gewtype-card-title">Haus (Neubau)</div>
                  </div>

                  {/* Card 4: Haus (Bestand) */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.HAUS_BESTAND ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.HAUS_BESTAND,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <HausIcon />
                    </div>
                    <div className="gewtype-card-title">Haus (Bestand)</div>
                  </div>

                  {/* Card 5: Wohnung (Neubau) */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.NEUBAU ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.NEUBAU,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <WohnungNeubauIcon />
                    </div>
                    <div className="gewtype-card-title">Wohnung (Neubau)</div>
                  </div>

                  {/* Card 6: Wohnung (Bestand) */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.BESTAND ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.BESTAND,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <WohnungBestandIcon />
                    </div>
                    <div className="gewtype-card-title">Wohnung (Bestand)</div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Address, Area, Price */}
            {step === 2 && (
              <div className="detail-list" style={{ width: '100%' }}>
                <h2
                  style={{
                    margin: '0 0 24px 0',
                    color: '#f3f1ff',
                    fontSize: '20px',
                    fontWeight: 400,
                  }}
                >
                  Adresse & Finanzdaten
                </h2>
                {/* Row 1: Straße */}
                <div className="wizard-form-row">
                  <div
                    className="wizard-field"
                    style={{ position: 'relative' }}
                  >
                    <label className="wizard-field-label">Straße*</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="text"
                        className="wizard-input"
                        value={property.streetName || ''}
                        onChange={handleAddressChange}
                        onKeyDown={handleSuggestionsKeyDown}
                        placeholder="Straßenname eingeben..."
                        onBlur={() => {
                          setTimeout(() => {
                            setSuggestions([]);
                            setSelectedSuggestionIndex(-1);
                          }, 250);
                        }}
                      />
                    </div>
                    {isSearching && (
                      <div className="suggestion-searching">
                        <LoadingSpinner size="small" color="white" />
                        <span>Wird gesucht...</span>
                      </div>
                    )}
                    {suggestions.length > 0 && (
                      <ul className="suggestions-container">
                        {suggestions.map((suggestion, index) => {
                          const displayText =
                            suggestion.text?.text ||
                            suggestion.placePrediction?.text?.text ||
                            suggestion.text ||
                            suggestion.placePrediction?.structuredFormat
                              ?.mainText?.text ||
                            'Unknown';
                          const secondaryText =
                            suggestion.placePrediction?.structuredFormat
                              ?.secondaryText?.text;
                          const isSelected = index === selectedSuggestionIndex;

                          return (
                            <li
                              key={index}
                              onClick={() => handleSuggestionClick(suggestion)}
                              onMouseDown={(e) => e.preventDefault()}
                              onMouseEnter={() =>
                                setSelectedSuggestionIndex(index)
                              }
                              className={`suggestion-item ${isSelected ? 'selected' : ''}`}
                            >
                              <div className="suggestion-main-text">
                                {displayText}
                              </div>
                              {secondaryText && (
                                <div className="suggestion-secondary-text">
                                  {secondaryText}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Row 2: Hausnummer and Stock */}
                <div className="wizard-form-row">
                  <div className="wizard-field">
                    <label className="wizard-field-label">Hausnummer*</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="text"
                        className="wizard-input"
                        value={property.houseNumber || ''}
                        placeholder="Nr."
                        onChange={(e) => {
                          const val = e.target.value;
                          setProperty((prev) => {
                            const updated = {
                              ...prev,
                              houseNumber: val,
                              latitude: undefined,
                              longitude: undefined,
                            };
                            const parts = [];
                            if (updated.streetName)
                              parts.push(updated.streetName);
                            if (updated.houseNumber)
                              parts.push(updated.houseNumber);
                            if (updated.city) parts.push(updated.city);
                            if (updated.country) parts.push(updated.country);
                            updated.address =
                              parts.join(', ') || prev.address || '';
                            return updated;
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="wizard-field">
                    <label className="wizard-field-label">Stock</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="text"
                        className="wizard-input"
                        value={stock}
                        placeholder="Stockwerk eingeben..."
                        onChange={(e) => setStock(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 3: Land and PLZ */}
                <div className="wizard-form-row">
                  <div className="wizard-field">
                    <label className="wizard-field-label">Land*</label>
                    <div className="wizard-field-input-box disabled">
                      <input
                        type="text"
                        className="wizard-input"
                        value="Österreich"
                        disabled
                      />
                      <span className="wizard-input-icon">
                        <svg
                          width="10"
                          height="5"
                          viewBox="0 0 10 5"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M0 0L5 5L10 0H0Z" fill="#F3F1FF" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="wizard-field">
                    <label className="wizard-field-label">PLZ</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="text"
                        className="wizard-input"
                        value={plz}
                        placeholder="PLZ eingeben..."
                        onChange={(e) => setPlz(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 4: Ort and Kaufpreis */}
                <div className="wizard-form-row">
                  <div
                    className="wizard-field"
                    style={{ position: 'relative' }}
                  >
                    <label className="wizard-field-label">Ort*</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="text"
                        className="wizard-input"
                        value={property.city || ''}
                        onChange={handleCityChange}
                        placeholder="Ort eingeben..."
                      />
                    </div>
                  </div>
                  {(property.gewType === GewType.NEUBAU ||
                    property.gewType === GewType.BESTAND ||
                    property.gewType === GewType.HAUS_NEUBAU ||
                    property.gewType === GewType.HAUS_BESTAND) && (
                    <div className="wizard-field">
                      <label className="wizard-field-label">Wohnfläche*</label>
                      <div className="wizard-field-input-box">
                        <input
                          type="number"
                          className="wizard-input"
                          style={{ paddingRight: '48px' }}
                          value={
                            property.area !== undefined ? property.area : ''
                          }
                          onChange={(e) => {
                            const val =
                              e.target.value === ''
                                ? undefined
                                : parseFloat(e.target.value);
                            setProperty((prev) => ({ ...prev, area: val }));
                          }}
                          placeholder="Fläche in m² eingeben..."
                          min="0"
                          step="any"
                        />
                        <span className="wizard-input-suffix">m²</span>
                      </div>
                    </div>
                  )}
                  {(property.gewType === GewType.GRUNDSTUECK ||
                    property.gewType === GewType.HAUS_NEUBAU ||
                    property.gewType === GewType.HAUS_BESTAND) && (
                    <div className="wizard-field">
                      <label className="wizard-field-label">
                        Grundstücksfläche*
                      </label>
                      <div className="wizard-field-input-box">
                        <input
                          type="number"
                          className="wizard-input"
                          style={{ paddingRight: '48px' }}
                          value={
                            property.plotArea !== undefined
                              ? property.plotArea
                              : ''
                          }
                          onChange={(e) => {
                            const val =
                              e.target.value === ''
                                ? undefined
                                : parseFloat(e.target.value);
                            setProperty((prev) => ({ ...prev, plotArea: val }));
                          }}
                          placeholder="Fläche in m² eingeben..."
                          min="0"
                          step="any"
                        />
                        <span className="wizard-input-suffix">m²</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Row 6: Baujahr */}
                <div className="wizard-form-row">
                  <div className="wizard-field">
                    <label className="wizard-field-label">Baujahr</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="number"
                        className="wizard-input"
                        value={
                          property.baujahr !== undefined ? property.baujahr : ''
                        }
                        onChange={(e) => {
                          const val =
                            e.target.value === ''
                              ? undefined
                              : parseInt(e.target.value, 10);
                          setProperty((prev) => ({ ...prev, baujahr: val }));
                        }}
                        placeholder="Jahr eingeben (z.B. 2015)..."
                        min="1000"
                        max={new Date().getFullYear() + 5}
                      />
                    </div>
                  </div>
                  <div className="wizard-field"></div>
                </div>
              </div>
            )}

            {/* Step 3: Features & Ratings */}
            {step === 3 && (
              <div style={{ width: '100%' }}>
                {loadingConfig ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <LoadingSpinner size="medium" color="white" />
                    <p style={{ marginTop: '12px' }}>
                      Konfiguration wird geladen...
                    </p>
                  </div>
                ) : !config ? (
                  <p style={{ color: '#ff6b6b', textAlign: 'center' }}>
                    Konfiguration konnte nicht geladen werden.
                  </p>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '40px',
                      width: '100%',
                    }}
                  >
                    {getSortedFeatures(config).length > 0 && (
                      <div className="attributes-grid">
                        {getSortedFeatures(config).map((f: any) =>
                          renderFeatureToggle(f.id, f.localization),
                        )}
                      </div>
                    )}

                    {config.weights?.ratings?.length > 0 && (
                      <div className="ratings-grid">
                        {config.weights.ratings.map((r: any) =>
                          renderRatingInput(r.id, r.localization),
                        )}
                      </div>
                    )}

                    {config.weights?.enumFeatures?.length > 0 && (
                      <div className="ratings-grid">
                        {config.weights.enumFeatures.map((f: any) =>
                          renderEnumFeatureDropdown(f),
                        )}
                      </div>
                    )}

                    {config.weights?.numericRatings?.length > 0 && (
                      <div className="ratings-grid">
                        {config.weights.numericRatings.map((r: any) =>
                          renderNumericRatingInput(
                            r.id,
                            r.localization,
                            r.step,
                          ),
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Finanzierung */}
            {step === 4 && (
              <div className="detail-list" style={{ width: '100%' }}>
                <h2
                  style={{
                    margin: '0 0 24px 0',
                    color: '#f3f1ff',
                    fontSize: '20px',
                    fontWeight: 400,
                  }}
                >
                  Finanzierung
                </h2>

                <div className="wizard-form-row">
                  <div className="wizard-field">
                    <label className="wizard-field-label">Kaufpreis</label>
                    <div className="wizard-field-input-box">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="wizard-input"
                        style={{ paddingRight: '40px' }}
                        value={
                          property.kaufpreis !== undefined
                            ? Number(property.kaufpreis).toLocaleString('de-DE')
                            : ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, '');
                          const val = raw === '' ? undefined : parseFloat(raw);
                          setProperty((prev) => ({
                            ...prev,
                            kaufpreis: val,
                            gain: (prev.currentValue || 0) - (val || 0),
                            gainPercentage:
                              val && val > 0
                                ? (((prev.currentValue || 0) - val) / val) * 100
                                : 0,
                          }));
                        }}
                        placeholder="z. B. 400.000"
                      />
                      <span className="wizard-input-suffix">€</span>
                    </div>
                  </div>
                  <div className="wizard-field">
                    <label className="wizard-field-label">Kaufdatum</label>
                    <DatePicker
                      value={property.kaufdatum}
                      onChange={(val) =>
                        setProperty((prev) => ({
                          ...prev,
                          kaufdatum: val,
                        }))
                      }
                      style={{ paddingRight: '48px' }}
                    />
                  </div>
                </div>

                {((property.kaufpreis !== undefined &&
                  !property.kaufdatum?.trim()) ||
                  (property.kaufpreis === undefined &&
                    !!property.kaufdatum?.trim())) && (
                  <div
                    style={{
                      color: '#ff4d4f',
                      fontSize: '13px',
                      marginTop: '-8px',
                    }}
                  >
                    Bitte geben Sie sowohl Kaufpreis als auch Kaufdatum an oder
                    lassen Sie beide Felder leer.
                  </div>
                )}

                <div className="wizard-form-row">
                  <div className="valuera-switch-container">
                    <div className="valuera-switch-label">Fremdfinanziert</div>
                    <div
                      className="valuera-switch-wrapper"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setProperty((prev) => ({
                          ...prev,
                          fremdfinanziert: !prev.fremdfinanziert,
                        }));
                      }}
                    >
                      <div
                        className={`valuera-switch-track ${property.fremdfinanziert ? 'on' : 'off'}`}
                      >
                        <div className="valuera-switch-circle" />
                      </div>
                      <span className="valuera-switch-status">
                        {property.fremdfinanziert ? 'Ja' : 'Nein'}
                      </span>
                    </div>
                  </div>
                </div>

                {property.fremdfinanziert && (
                  <>
                    <div className="wizard-form-row">
                      <div className="wizard-field">
                        <label className="wizard-field-label">
                          Eigenkapital
                        </label>
                        <div className="wizard-field-input-box">
                          <input
                            type="number"
                            className="wizard-input"
                            style={{ paddingRight: '40px' }}
                            value={
                              property.eigenkapital !== undefined
                                ? property.eigenkapital
                                : ''
                            }
                            onChange={(e) =>
                              setProperty((prev) => ({
                                ...prev,
                                eigenkapital:
                                  e.target.value === ''
                                    ? undefined
                                    : parseFloat(e.target.value),
                              }))
                            }
                            placeholder="z. B. 50.000"
                            min="0"
                          />
                          <span className="wizard-input-suffix">€</span>
                        </div>
                      </div>
                      <div className="wizard-field">
                        <label className="wizard-field-label">
                          Fremdkapital
                        </label>
                        <div className="wizard-field-input-box">
                          <input
                            type="number"
                            className="wizard-input"
                            style={{ paddingRight: '40px' }}
                            value={
                              property.fremdkapital !== undefined
                                ? property.fremdkapital
                                : ''
                            }
                            onChange={(e) =>
                              setProperty((prev) => ({
                                ...prev,
                                fremdkapital:
                                  e.target.value === ''
                                    ? undefined
                                    : parseFloat(e.target.value),
                              }))
                            }
                            placeholder="z. B. 250.000"
                            min="0"
                          />
                          <span className="wizard-input-suffix">€</span>
                        </div>
                      </div>
                    </div>

                    {property.eigenkapital !== undefined &&
                      property.fremdkapital !== undefined &&
                      property.kaufpreis !== undefined &&
                      property.eigenkapital + property.fremdkapital !==
                        property.kaufpreis && (
                        <div
                          style={{
                            color: '#ff4d4f',
                            fontSize: '13px',
                            marginTop: '-8px',
                          }}
                        >
                          Eigenkapital + Fremdkapital muss dem Kaufpreis
                          entsprechen.
                        </div>
                      )}

                    <div className="wizard-form-row">
                      <div className="wizard-field">
                        <label className="wizard-field-label">Zinssatz</label>
                        <div className="wizard-field-input-box">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="wizard-input"
                            style={{ paddingRight: '40px' }}
                            value={zinssatzText}
                            onChange={(e) => {
                              const val = e.target.value.replace('.', ',');
                              if (/^[0-9]*[,]?[0-9]*$/.test(val)) {
                                setZinssatzText(val);
                                const parsed = parseFloat(
                                  val.replace(',', '.'),
                                );
                                setProperty((prev) => ({
                                  ...prev,
                                  zinssatz:
                                    !isNaN(parsed) && val !== ''
                                      ? parsed
                                      : undefined,
                                }));
                              }
                            }}
                            placeholder="z. B. 3,5"
                          />
                          <span className="wizard-input-suffix">%</span>
                        </div>
                      </div>
                      <div className="wizard-field">
                        <label className="wizard-field-label">
                          Monatliche Rate
                        </label>
                        <div className="wizard-field-input-box">
                          <input
                            type="number"
                            className="wizard-input"
                            style={{ paddingRight: '40px' }}
                            value={
                              property.monatlicheRate !== undefined
                                ? property.monatlicheRate
                                : ''
                            }
                            onChange={(e) =>
                              setProperty((prev) => ({
                                ...prev,
                                monatlicheRate:
                                  e.target.value === ''
                                    ? undefined
                                    : parseFloat(e.target.value),
                              }))
                            }
                            placeholder="z. B. 1.200"
                            min="0"
                          />
                          <span className="wizard-input-suffix">€</span>
                        </div>
                      </div>
                      <div className="wizard-field">
                        <label className="wizard-field-label">Laufzeit</label>
                        <div className="wizard-field-input-box">
                          <input
                            type="number"
                            className="wizard-input"
                            value={
                              property.laufzeit !== undefined
                                ? property.laufzeit
                                : ''
                            }
                            onChange={(e) =>
                              setProperty((prev) => ({
                                ...prev,
                                laufzeit:
                                  e.target.value === ''
                                    ? undefined
                                    : parseInt(e.target.value, 10),
                              }))
                            }
                            placeholder="z. B. 25"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}


            {/* Stepper Navigation Buttons */}
            <div
              className={`wizard-buttons-container ${step === 1 ? 'no-border' : ''}`}
            >
              {step > 1 && (
                <button
                  type="button"
                  className="action-button secondary"
                  onClick={prevStep}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: '8px' }}
                  >
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                  Zurück
                </button>
              )}
              {step < 4 ? (
                <button
                  type="button"
                  className="action-button primary"
                  onClick={nextStep}
                  disabled={
                    (step === 1 && !property.gewType) ||
                    (step === 2 && (!isStep2Valid() || isGeocoding)) ||
                    (step === 3 && !config)
                  }
                >
                  {isGeocoding ? (
                    'Wird überprüft...'
                  ) : (
                    <>
                      Weiter
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginLeft: '8px' }}
                      >
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  className="action-button primary"
                  onClick={handleSave}
                  disabled={saving || !isStep4Valid()}
                >
                  {saving ? (
                    uploadProgressText || 'Wird gespeichert...'
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: '8px' }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                      Immobilie hinzufügen
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePropertyPage;
